/**
 * Google One Tap exponential cooldown tracking.
 * Google suppresses One Tap for increasing durations after user dismissal:
 *   1st dismiss → 2 hours, 2nd → 1 day, 3rd → 7 days, 4th+ → 28 days
 * We mirror this client-side to skip unnecessary initialization attempts.
 */

const COOLDOWN_KEY = "prezntz_onetap_cooldown";

interface CooldownState {
  dismissCount: number;
  cooldownUntil: number; // epoch ms
}

const COOLDOWN_DURATIONS_MS = [
  2 * 60 * 60 * 1000,       // 2 hours
  24 * 60 * 60 * 1000,      // 1 day
  7 * 24 * 60 * 60 * 1000,  // 7 days
  28 * 24 * 60 * 60 * 1000, // 28 days
];

function getCooldownState(): CooldownState | null {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isInCooldown(): boolean {
  const state = getCooldownState();
  if (!state) return false;
  if (Date.now() < state.cooldownUntil) return true;
  localStorage.removeItem(COOLDOWN_KEY);
  return false;
}

export function recordDismissal(): void {
  const state = getCooldownState();
  const dismissCount = (state?.dismissCount ?? 0) + 1;
  const durationIndex = Math.min(dismissCount - 1, COOLDOWN_DURATIONS_MS.length - 1);
  const duration = COOLDOWN_DURATIONS_MS[durationIndex];
  localStorage.setItem(COOLDOWN_KEY, JSON.stringify({ dismissCount, cooldownUntil: Date.now() + duration }));
}

export function resetCooldown(): void {
  localStorage.removeItem(COOLDOWN_KEY);
}
